<template>
  <BModal
    :modal-name="modalName"
    width="400px"
    height="auto"
  >
    <BModalHeader
      :title="$t('productCategoryAssociation.addTitle')"
      @modal-close="handleClose"
    />
    <BModalBody>
      <form @submit.prevent="handleAdd">
        <div class="form-group">
          <label
            for="product-category-select"
            class="form-label"
          >{{ $t('productCategory.title') }}</label>
          <BSelect
            id="product-category-select"
            v-model="selectedCategoryId"
            :placeholder="$t('productCategory.title')"
            class="full-width"
            :items="productCategories"
            item-text="name"
            item-value="id"
          />
        </div>
        <div class="form-group">
          <label
            for="product-category-status-select"
            class="form-label"
          >{{ $t('productCategoryAssociation.status') }}</label>
          <BSelect
            id="product-category-status-select"
            v-model="selectedStatusId"
            :placeholder="$t('productCategoryAssociation.status')"
            class="full-width"
            :items="productCategoryStatuses"
            item-text="name"
            item-value="id"
          />
        </div>
        <div class="form-actions">
          <BBtn
            type="primary"
            btn-type="submit"
            :disabled="!isFormValid"
            :loading="submitting"
          >
            {{ $t('productCategoryAssociation.add') }}
          </BBtn>
        </div>
      </form>
    </BModalBody>
  </BModal>
</template>

<script lang="ts" setup>
import { ref, computed, inject } from 'vue';
import type { ProductCategory, ProductCategoryStatus } from '@/api/openapi';
import { KEY_OF_MODAL } from '@/injection-keys';

const props = defineProps<{
  modalName: string;
  productCategories: ProductCategory[];
  productCategoryStatuses: ProductCategoryStatus[];
  submitting: boolean;
}>();

const emit = defineEmits<{
  add: [productCategoryId: number, productCategoryStatusId: number];
}>();

const modal = inject(KEY_OF_MODAL);

const selectedCategoryId = ref<number | null>(null);
const selectedStatusId = ref<number | null>(null);

const isFormValid = computed(() => selectedCategoryId.value != null && selectedStatusId.value != null);

const resetForm = () => {
  selectedCategoryId.value = null;
  selectedStatusId.value = null;
};

const handleClose = () => {
  resetForm();
  modal.hide(props.modalName);
};

const handleAdd = () => {
  if (!isFormValid.value || props.submitting) return;
  emit('add', selectedCategoryId.value, selectedStatusId.value);
};

defineExpose({ resetForm, handleClose });
</script>

<style lang="scss" scoped>
.form-group {
  margin-bottom: $basespace-400;
}

.form-label {
  display: block;
  font-size: $fontsize-100;
  color: $textcolor-light;
  margin-bottom: $basespace-100;
}

.full-width {
  width: 100%;
}

.form-actions {
  display: flex;
  justify-content: center;
  margin-top: $basespace-400;
}
</style>
